import type { MemorisationStat, ScriptEntry } from "./types";

export function updateMemorisationStat(previous: MemorisationStat | undefined, score: number, threshold: number, revealed = false, now = new Date()): MemorisationStat {
  const attempts = (previous?.attempts ?? 0) + 1;
  const passed = score >= threshold;
  const intervalDays = passed ? Math.max(1, previous?.intervalDays ? previous.intervalDays * 2 : 1) : 0;
  const due = new Date(now);
  due.setDate(due.getDate() + intervalDays);
  return {
    attempts,
    successes: passed ? (previous?.successes ?? 0) + 1 : Math.max(0, (previous?.successes ?? 0) - 1),
    averageScore: (((previous?.averageScore ?? 0) * (attempts - 1)) + score) / attempts,
    intervalDays,
    dueAt: due.toISOString(),
    lastAttemptAt: now.toISOString(),
    revealCount: (previous?.revealCount ?? 0) + (revealed ? 1 : 0),
  };
}

export function selectSmartReviewLines(lines: ScriptEntry[], stats: Record<string, MemorisationStat>, selectedCharacterIds: string[], threshold: number, now = new Date(), limit = 20): ScriptEntry[] {
  const selected = new Set(selectedCharacterIds);
  const candidates = selected.size ? lines.filter((line) => selected.has(line.speakerId ?? "")) : lines;
  const timestamp = now.getTime();
  const eligible = candidates.filter((line) => {
    const stat = stats[line.id];
    return !stat || new Date(stat.dueAt).getTime() <= timestamp || stat.averageScore < threshold;
  });
  const byPriority = (left: ScriptEntry, right: ScriptEntry): number => {
    const leftStat = stats[left.id];
    const rightStat = stats[right.id];
    const leftDue = !leftStat || new Date(leftStat.dueAt).getTime() <= timestamp ? 0 : 1;
    const rightDue = !rightStat || new Date(rightStat.dueAt).getTime() <= timestamp ? 0 : 1;
    if (leftDue !== rightDue) return leftDue - rightDue;
    return (leftStat?.averageScore ?? -1) - (rightStat?.averageScore ?? -1);
  };
  const review = [...eligible].sort(byPriority).slice(0, limit);
  if (review.length) return review;
  return [...candidates].sort((left, right) => (stats[left.id]?.averageScore ?? 1) - (stats[right.id]?.averageScore ?? 1)).slice(0, Math.min(5, limit));
}

export function smartRevealMode(successes: number): "normal" | "initials" | "hidden" {
  if (successes >= 2) return "hidden";
  if (successes === 1) return "initials";
  return "normal";
}
